import React from 'react'
import ReactDOM from 'react-dom'
import { Provider, useDispatch } from 'react-redux'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { browserTracingIntegration } from '@sentry/react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { AnyAction } from 'redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ThunkDispatch } from 'redux-thunk'
import { QueryParamProvider } from 'use-query-params'

import { configureStore, IApplicationState } from 'setup/store'

import { ROUTE_PATHS } from 'router/routes'

import {
  // BetbySportModule,
  DigitainSportModule,
  FaqPageModule,
  HelpPageModule,
  MainPageModule,
  ProfilePageModule,
  SlotsModule,
  // SoftionSportModule,
  TermsPageModule,
  WheelGamePageModule,
} from 'modules'
import { createRoutes } from 'modules/createRoutes'
import SlotFrame from 'modules/SlotsRoot/components/SlotFrame/SlotFrame'
import SlotRedirect from 'modules/SlotsRoot/components/SlotRedirect/SlotRedirect'

// import CheckGeoAccess from 'shared/components/CheckGeoAccess'
import DefineDeviceComponent from 'components/DefineDeviceComponent'
import { SessionPolling } from 'components/SessionPolling'
import SetAppLanguage from 'components/SetAppLanguage/SetAppLanguage'

import App from './App'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import Api from './services/api'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'

import 'react-toastify/dist/ReactToastify.css'
import '@bcraft-frontend-lib/core/lib/style.css'
import '@bcraft-frontend-lib/last-wins/lib/style.css'
import './index.scss'

dayjs.extend(utc)
dayjs.extend(timezone)

const api = new Api('')

const modules = [
  // new ProfilePageModule(),
  new TermsPageModule(),
  new HelpPageModule(),
  new FaqPageModule(),
  new SlotsModule(),
  new DigitainSportModule(),
  new MainPageModule(),
  new WheelGamePageModule(),
  // new BetbySportModule(),
  // new SoftionSportModule(),
]

const childrens = createRoutes(modules)

export const { store, persistor } = configureStore({ api })

export type AppDispatch = typeof store.dispatch

export const useAppDispatch = () => useDispatch<ThunkDispatch<IApplicationState, any, AnyAction>>()

Sentry.init({
  dsn: 'https://4b7a5a867502bb3dfb955af52b32aaa5@o4509388812713984.ingest.de.sentry.io/4509434827505745',
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  release: process.env.SENTRY_PROJECT_RELEASE,
  integrations: [browserTracingIntegration()],
  tracePropagationTargets: [/^https:\/\/tinbetplay\.net\/api/],
  beforeSend(event) {
    if (event.level !== 'fatal') return null
    return event
  },
})

ReactDOM.render(
  <BrowserRouter>
    <QueryParamProvider ReactRouterRoute={Route}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ErrorBoundary>
            <SetAppLanguage />
            <SessionPolling>
              <Switch>
                <Route
                  key={ROUTE_PATHS.SLOTS_PROVIDER_FREE_SPIN}
                  exact
                  path={ROUTE_PATHS.SLOTS_PROVIDER_FREE_SPIN}
                  component={() => (
                    <DefineDeviceComponent mobileComponent={<SlotRedirect />} desktopComponent={<SlotFrame />} />
                  )}
                />
                <Route
                  key={ROUTE_PATHS.SLOTS_PROVIDER}
                  exact
                  path={ROUTE_PATHS.SLOTS_PROVIDER}
                  component={() => (
                    <DefineDeviceComponent mobileComponent={<SlotRedirect />} desktopComponent={<SlotFrame />} />
                  )}
                />
                <Route>
                  <App>{childrens}</App>
                </Route>
              </Switch>
            </SessionPolling>
          </ErrorBoundary>
        </PersistGate>
      </Provider>
    </QueryParamProvider>
  </BrowserRouter>,
  document.getElementById('root'),
)

// serviceWorkerRegistration.register({
//   onUpdate: (registration: ServiceWorkerRegistration) => {
//     registration.waiting?.postMessage({ type: 'SKIP_WAITING' })
//     window.location.reload()
//   },
// })

serviceWorkerRegistration.unregister()
